/**
 * PanelFooter — the booking panel's bottom action bar.
 * One full-width primary button; a centered hint when the form is incomplete.
 * Edit: Cancel (outline, left) beside Save changes (primary, the rest of the row), one line; Cancel closes without saving.
 */

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PanelFooterProps {
  mode: 'create' | 'edit' | 'reschedule' | 'cancel';
  isSubmitting: boolean;
  isValid: boolean;
  missingFields?: string[];
  rawErrors?: unknown;
  onSave: () => void;
  onCancel: () => void;
  onReschedule?: () => void;
  onCancelAppointment?: () => void;
  labels?: { save?: string; saving?: string; cancel?: string };
  /** When true the panel is inside a modal — heavier top border, more vertical padding. */
  modal?: boolean;
}

/** Turn raw RHF field paths into human-readable names, in booking flow order. */
const FIELD_LABELS: Record<string, string> = {
  'provider.id': 'a provider',
  'provider': 'a provider',
  'providerId': 'a provider',
  'location.id': 'a location',
  'location': 'a location',
  'appointmentProfile.id': 'an appointment type',
  'appointmentProfile': 'an appointment type',
  'profileId': 'an appointment type',
  'appointmentProfileId': 'an appointment type',
  'slotId': 'a date and time',
  'slotStart': 'a date and time',
  'slot': 'a date and time',
  'date': 'a date',
  'selectedDate': 'a date',
  'patient.id': 'a patient',
  'patient': 'a patient',
  'patientId': 'a patient',
  'reasonCode': 'a reason for visit',
  'reasonCode.code': 'a reason for visit',
  'referenceAppointmentId': 'a previous appointment',
  'residentialAddressId': 'an address',
  'reminderProfileId': 'a reminder',
  'sponsorId': 'an agreement',
}

/** Booking flow order — show the hint for the NEXT step only. */
const FLOW_ORDER: { key: string; target: string }[] = [
  { key: 'providerId', target: 'provider' },
  { key: 'location', target: 'location' },
  { key: 'appointmentProfileId', target: 'visit-type' },
  { key: 'reasonCode', target: 'reason' },
  { key: 'slotId', target: 'slot' },
  { key: 'patientId', target: 'patient' },
  { key: 'referenceAppointmentId', target: 'previous-appointment' },
  { key: 'residentialAddressId', target: 'address' },
  { key: 'reminderProfileId', target: 'reminder' },
  { key: 'sponsorId', target: 'receipt' },
]

function nextHint(fields: string[]): { label: string; target: string } | null {
  for (const { key, target } of FLOW_ORDER) {
    const label = FIELD_LABELS[key]
    if (label && fields.some((f) => FIELD_LABELS[f] === label)) {
      return { label, target }
    }
  }
  const first = fields[0]
  if (!first) return null
  return { label: FIELD_LABELS[first] ?? first.replace(/([A-Z])/g, ' $1').replace(/\./g, ' ').trim().toLowerCase(), target: '' }
}

function scrollTo(target: string) {
  const el = document.querySelector(`[data-completion-target="${target}"]`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  // A target may mark its control with data-completion-focus: focus it without a second scroll (opens nothing).
  el?.querySelector<HTMLElement>('[data-completion-focus]')?.focus({ preventScroll: true })
}

export function PanelFooter({
  mode,
  isSubmitting,
  isValid,
  missingFields,
  onSave,
  onCancel,
  onReschedule,
  onCancelAppointment,
  labels,
  modal = false,
}: PanelFooterProps) {
  const wrapCls = cn(
    'flex flex-col gap-2 flex-shrink-0',
    modal ? 'px-6 py-6 border-t-2' : 'px-4 py-4 border-t',
    'border-border-default',
  );

  if (mode === 'cancel') {
    return (
      <div className={wrapCls}>
        <Button
          variant="destructive"
          size="lg"
          className="w-full"
          onClick={onCancelAppointment}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Cancelling...' : 'Cancel appointment'}
        </Button>
        <Button variant="outline" size="lg" className="w-full" onClick={onCancel}>
          {labels?.cancel ?? 'Close'}
        </Button>
      </div>
    );
  }

  const next = !isValid && missingFields && missingFields.length > 0
    ? nextHint(missingFields)
    : null

  const saveButton = (className: string) => (
    <Button size="lg" className={className} onClick={onSave} disabled={!isValid || isSubmitting}>
      {isSubmitting
        ? (labels?.saving ?? 'Saving...')
        : (labels?.save ?? (mode === 'create' ? 'Book appointment' : 'Save changes'))}
    </Button>
  )

  return (
    <div className={wrapCls}>
      {mode === 'edit' && onReschedule && (
        <Button variant="outline" size="lg" className="w-full" onClick={onReschedule}>
          Reschedule
        </Button>
      )}
      {next && (
        <p className="booking-foot-hint">
          Select{' '}
          <button type="button" onClick={() => scrollTo(next.target)} className="booking-foot-hint-link">
            {next.label}
          </button>{' '}
          to continue
        </p>
      )}
      {mode === 'edit' ? (
        <div className="flex gap-2">
          <Button variant="outline" size="lg" className="whitespace-nowrap" onClick={onCancel} disabled={isSubmitting}>
            {labels?.cancel ?? 'Cancel'}
          </Button>
          {saveButton('min-w-0 flex-1 whitespace-nowrap')}
        </div>
      ) : (
        <>
          {saveButton('w-full')}
          {/* Dismiss without saving (main kept this beside the primary action). */}
          <Button variant="outline" size="lg" className="w-full" onClick={onCancel}>
            {labels?.cancel ?? 'Close'}
          </Button>
        </>
      )}
    </div>
  )
}
